import { NavLink, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingCart, Menu, LayoutDashboard, CreditCard, Briefcase } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { Logo } from "./Logo";
import { MotionSelect, LanguageMenu } from "./Selectors";
import { useApp } from "../context/AppContext";
import { useState } from "react";
import { MegaMenu, MobileCategoryBrowser } from "./CategoryMenu";
import { NotificationBell } from "./seller/SellerTools";

import { tx, useLang } from "../lib/i18n";
const NAV = [
  ["/shops", "nav.shops", "shops"], ["/collections", "nav.collections", "collections"], ["/drops", "nav.drops", "drops"],
  ["/about", "nav.about", "about"], ["/track-order", "nav.track", "track-order"],
];

const IconBtn = ({ children, badge, testid, label, onClick }) => { useLang(); return (
  <button onClick={onClick} data-testid={testid} aria-label={tx(label)} className="relative grid h-10 w-10 place-items-center rounded-full text-[var(--mv-text)] hover:bg-white/5 transition-colors">
    {children}
    {tx(badge > 0 && (
      <span data-testid={`${testid}-badge`} className="absolute right-0.5 top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-[var(--mv-teal)] px-1 text-[9px] font-bold text-[#05090b]">{tx(badge)}</span>
    ))}
  </button>
); };

export const AnnouncementBar = ({ settings }) => { useLang(); return (
  <div className="border-b border-[var(--mv-border)] bg-[var(--mv-bg)]" data-testid="announcement-bar">
    <div className="mv-container flex h-9 items-center justify-between gap-3">
      <div className="hidden sm:block"><LanguageMenu /></div>
      <p className="flex-1 truncate text-center text-[11px] sm:text-xs text-[var(--mv-muted)]" data-testid="announcement-text">
        {tx(settings?.announcement_enabled !== false ? settings?.announcement : "")}
      </p>
      <MotionSelect />
    </div>
  </div>
); };

export const Header = ({ settings }) => { useLang();
  const { t, counts, user, requireAuth, setCartOpen, setSearchOpen } = useApp();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const link = ({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-[var(--mv-teal)]" : "text-[var(--mv-text)] hover:text-[var(--mv-teal)]"}`;
  return (
    <header className="sticky top-0 z-40 border-b border-[var(--mv-border)] bg-[rgba(5,9,11,0.82)] backdrop-blur-xl" data-testid="site-header">
      <div className="mv-container flex h-[72px] items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger className="lg:hidden grid h-11 w-11 place-items-center rounded-full hover:bg-white/5" data-testid="mobile-menu-trigger" aria-label={t("nav.menu")}>
              <Menu className="h-5 w-5" />
            </SheetTrigger>
            <SheetContent side="left" className="w-full max-w-full sm:max-w-md p-0 bg-[var(--mv-bg)] border-[var(--mv-border-2)] text-[var(--mv-text)] [&>button]:hidden">
              <SheetTitle className="sr-only">{t("nav.menu")}</SheetTitle>
              <MobileCategoryBrowser onClose={() => setMenuOpen(false)} nav={
                <div className="flex flex-col px-3 pt-2" onClick={() => setMenuOpen(false)}>
                  {NAV.map(([to, k, id]) => <NavLink key={to} to={to} className={({ isActive }) => `${link({ isActive })} flex min-h-[44px] items-center text-base`} data-testid={`mobile-nav-${id}`}>{t(k)}</NavLink>)}
                  <NavLink to={user ? "/wallet" : "/cards"} className={({ isActive }) => `${link({ isActive })} flex min-h-[44px] items-center text-base`} data-testid="mobile-nav-card">{tx("My MIVO Card")}</NavLink>
                  {user?.role === "super_admin" && <NavLink to="/admin" className={({ isActive }) => `${link({ isActive })} flex min-h-[44px] items-center text-base`} data-testid="mobile-nav-admin">{t("nav.admin")}</NavLink>}
                  {["agent", "seller"].includes(user?.role) && <NavLink to="/partner" className={({ isActive }) => `${link({ isActive })} flex min-h-[44px] items-center text-base`} data-testid="mobile-nav-partner">{tx("My Stores")}</NavLink>}
                  <div className="pt-3" onClick={(e) => e.stopPropagation()}><LanguageMenu /></div>
                </div>} />
            </SheetContent>
          </Sheet>
          <Logo tagline={settings?.tagline} />
        </div>
        <nav className="hidden lg:flex items-center gap-8" aria-label={tx("Main")}>
          <MegaMenu />
          {NAV.map(([to, k, id]) => <NavLink key={to} to={to} className={link} data-testid={`nav-${id}`}>{t(k)}</NavLink>)}
        </nav>
        <div className="flex items-center gap-0.5 sm:gap-1.5">
          <IconBtn testid="header-search-button" label={t("common.search")} onClick={() => setSearchOpen(true)}><Search className="h-[19px] w-[19px]" /></IconBtn>
          {tx(user?.role === "super_admin" && (
            <span className="hidden sm:inline-flex"><IconBtn testid="header-admin-button" label={t("nav.admin")} onClick={() => nav("/admin")}><LayoutDashboard className="h-[19px] w-[19px]" /></IconBtn></span>
          ))}
          {tx(["agent", "seller"].includes(user?.role) && (
            <span className="hidden sm:inline-flex"><IconBtn testid="header-partner-button" label={tx("Partner portal")} onClick={() => nav("/partner")}><Briefcase className="h-[19px] w-[19px]" /></IconBtn></span>
          ))}
          <span className="hidden sm:inline-flex"><IconBtn testid="header-card-button" label={tx("My MIVO Card")} onClick={() => nav(user ? "/wallet" : "/cards")}><CreditCard className="h-[19px] w-[19px]" /></IconBtn></span>
          {user && <NotificationBell />}
          <IconBtn testid="header-account-button" label={t("common.account")} onClick={() => nav(user ? "/account" : "/login")}><User className="h-[19px] w-[19px]" /></IconBtn>
          <IconBtn testid="header-wishlist-button" label={t("common.wishlist")} badge={counts.wishlist} onClick={() => requireAuth() && nav("/wishlist")}><Heart className="h-[19px] w-[19px]" /></IconBtn>
          <IconBtn testid="header-cart-button" label={t("common.cart")} badge={counts.cart} onClick={() => requireAuth() && setCartOpen(true)}><ShoppingCart className="h-[19px] w-[19px]" /></IconBtn>
        </div>
      </div>
    </header>
  );
};
